import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SpaceProps } from 'styled-system';
import { AspectRatioBox, ScreenshotImage } from 'views/Summary/imageComponents';
import styled from 'styled-components';
import Divider from 'ui/Divider/Divider';
import AssemblyInstructionStep from 'ui/AssemblyInstructionStep/AssemblyInstructionStep';
import Body from 'ui/Typography/Body';
import { useIntl } from 'react-intl';
import {
  Product,
  ProductGoodToKnow,
  AssemblyInstructionsComponent
} from '@inter-ikea-storageone/range';
import screenshotSettings from 'config/screenshotSettings';
import { getAssemblyInstructions } from 'ducks/assemblyInstructions';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { InstructionStep } from 'ducks/assemblyInstructions';
import { IVector2 } from '@inter-ikea-betrakta/viewer';
import media from 'ui/media';
import { useScreenshot } from 'contexts/screenshot';
import { isMobile } from 'utils/platform';
import LoadingSpinner from 'ui/LoadingSpinner/LoadingSpinner';
import Title from 'ui/Typography/Title';
import { useCore } from 'contexts/core';
import { useServiceSettings } from 'contexts/serviceSettings';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50%;
`;

const ImportantToKnowContainer = styled.div`
  margin: ${({ theme }) => theme.space.l} 0 ${({ theme }) => theme.space.xl} 0;
  display: flex;
  ${media.mobile`
    display: block;
  `}
  @media print {
    page-break-inside: avoid;
  }
`;

const Wrapper = styled.div`
  @media print {
    page-break-inside: avoid;
  }
`;

const ImageContainer = styled.div`
  width: 49%;
  position: relative;
  ${media.mobile`
    width: 100%;
  `}
`;

const GoodToKnowList = styled.ul`
  float: left;
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const SvgContainer = styled.svg`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 2;
`;

const ImportantToKnowInstructions = styled.div`
  width: 49%;
  padding: 0 ${({ theme }) => theme.space.l};
  ${media.mobile`
    width: 100%;
    padding: 0;
  `}
`;

const PrintTitle = styled(Title)`
  display: none;
  @media print {
    display: block;
  }
`;

const TitleStyled = styled(Body)`
  font-weight: bold;
  margin-bottom: ${({ theme }) => theme.space.s};
  ${media.mobile`
    margin-top: ${({ theme }) => theme.space.s};
  `}
`;

const Text = styled(Body)`
  line-height: ${({ theme }) => theme.lineHeights.base};
`;

const SvgText = styled.text`
  font-size: 2.8rem;
`;

type Props = SpaceProps & {
  goodToKnow: ProductGoodToKnow[];
  productsInScene: Product[];
  shouldGenerate: boolean;
};

const AssemblyInstructions = ({ goodToKnow, shouldGenerate, ...props }: Props) => {
  const { theme } = useServiceSettings();
  const fontFamily = `${theme.fontFace.family}, ${theme.fontFace.fallbacks.join(',')}`;
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const productInstructions = useSelector(getAssemblyInstructions);
  const { assemblyMainImageData, captureAssemblyInstructions } = useScreenshot();
  const isEven = (val: number) => val % 2 === 0;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      assemblyMainImageData.length === 0 &&
      shouldGenerate &&
      coreApi.entities.getByComponent(AssemblyInstructionsComponent.id).length > 0
    ) {
      setLoading(true);
      setTimeout(() => {
        captureAssemblyInstructions();
      }, 200);
    }
  }, [assemblyMainImageData.length, captureAssemblyInstructions, coreApi.entities, shouldGenerate]);

  useEffect(() => {
    if (productInstructions.length > 0 || !shouldGenerate) {
      setLoading(false);
    }
  }, [productInstructions.length, shouldGenerate]);

  return loading ? (
    <LoadingWrapper>
      <LoadingSpinner />
    </LoadingWrapper>
  ) : (
    <Wrapper {...props} data-testid="assembly-instructions-test-id">
      <ImportantToKnowContainer>
        {productInstructions.length > 0 && (
          <ImageContainer>
            <AspectRatioBox>
              <SvgContainer
                viewBox={`0 0 ${screenshotSettings.default.size.x} ${screenshotSettings.default.size.y}`}
                fontFamily={fontFamily}
              >
                {assemblyMainImageData[0]?.points.map(
                  (imagePointPosition: IVector2, index: number) => {
                    return (
                      <Fragment key={index}>
                        <circle
                          cx={imagePointPosition?.x}
                          cy={imagePointPosition?.y}
                          r={40}
                          fill="white"
                        />
                        <SvgText
                          x={imagePointPosition?.x}
                          y={imagePointPosition?.y + 4}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {index + 1}
                        </SvgText>
                      </Fragment>
                    );
                  }
                )}
              </SvgContainer>
              {assemblyMainImageData[0]?.image && (
                <ScreenshotImage alt={'noImage'} src={assemblyMainImageData[0].image} />
              )}
            </AspectRatioBox>
          </ImageContainer>
        )}
        <ImportantToKnowInstructions>
          <TitleStyled type={ThemeFontStyleTypeEnum.bodyLarge}>
            {formatMessage({ id: 'storageone.importantToKnow' })}
          </TitleStyled>
          <GoodToKnowList>
            {goodToKnow &&
              goodToKnow.length > 0 &&
              goodToKnow.map((gtk: ProductGoodToKnow, index: number) => (
                <li key={index}>
                  <Text type={ThemeFontStyleTypeEnum.bodyLarge} mb="l" mt="s" key={index}>
                    {gtk.text}
                  </Text>
                </li>
              ))}
          </GoodToKnowList>
        </ImportantToKnowInstructions>
      </ImportantToKnowContainer>
      {!loading && !isMobile() && <Divider />}
      <PrintTitle type={ThemeFontStyleTypeEnum.headingLarge} mt="md">
        {formatMessage({ id: 'storageone.assemblyInstructions' })}
      </PrintTitle>
      {productInstructions &&
        productInstructions.length > 0 &&
        productInstructions.map((instructionStep: InstructionStep, index: number) => (
          <Fragment key={index}>
            <AssemblyInstructionStep
              image={instructionStep.imageData?.image && instructionStep.imageData.image}
              points={instructionStep.imageData?.points && instructionStep.imageData.points}
              instructionStep={instructionStep}
              width={isEven(index) ? -instructionStep.width : instructionStep.width}
              instructionStepTitle={`${formatMessage({ id: 'storageone.frame' })} ${
                instructionStep.instructionStepTitleCount
              }`}
              tableHeaders={[
                formatMessage({ id: 'storageone.hole' }),
                formatMessage({ id: 'storageone.articleNumber' })
              ]}
              noAssemblyInstructionsText={formatMessage({
                id: 'storageone.noAssemblyInstructionsAvailable'
              })}
            />
            <Divider hidden={index === productInstructions.length - 1} />
          </Fragment>
        ))}
    </Wrapper>
  );
};

export default AssemblyInstructions;
